import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  ChartLineUp, ShieldCheck, TrendUp, TrendDown, ArrowRight, UsersFour, Clock,
  Warning, XCircle, CheckCircle, Sparkle, FilePdf,
} from "@phosphor-icons/react";
import {
  AreaChart, Area, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid,
  BarChart, Bar, Legend,
} from "recharts";

const ROLE_LABEL = { coach: "Coaches", trainer: "Trainers", referee: "Referees", volunteer: "Volunteers", other: "Other" };

export default function Executive() {
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.get("/executive/overview").then((r) => setData(r.data));
  }, []);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/executive/overview.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `executive_snapshot_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Executive snapshot downloaded");
    } catch (_) {
      toast.error("PDF download failed");
    } finally {
      setDownloading(false);
    }
  };

  if (!data) return <div className="p-8 text-slate-400">Loading executive view…</div>;

  const scoreColor = data.audit_readiness_score >= 85 ? "text-emerald-300" : data.audit_readiness_score >= 60 ? "text-amber-300" : "text-rose-300";
  const scoreRing = data.audit_readiness_score >= 85 ? "border-emerald-500/30 bg-emerald-500/5" : data.audit_readiness_score >= 60 ? "border-amber-500/30 bg-amber-500/5" : "border-rose-500/30 bg-rose-500/5";

  // Trend arrow
  const trend = data.trend_12w;
  const trendDelta = trend.length >= 2 ? trend[trend.length - 1].score - trend[0].score : 0;

  const roleChart = Object.entries(data.by_role).map(([role, v]) => ({
    role: ROLE_LABEL[role] || role,
    Current: v.current,
    Expiring: v.expiring,
    Expired: v.expired,
    None: v.none,
  }));

  return (
    <div className="p-4 md:p-8 max-w-[1600px] mx-auto">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="font-label text-orange-400 mb-2">EXECUTIVE VIEW · {data.org_name}</div>
          <h1 className="text-4xl md:text-5xl font-display font-black tracking-tight">Audit-Readiness Overview</h1>
          <p className="text-slate-400 mt-2">Leadership snapshot — compliance trend, credential status, and upcoming actions.</p>
        </div>
        <Button
          data-testid="executive-pdf-button"
          onClick={downloadPdf}
          disabled={downloading}
          className="bg-orange-500 text-white hover:bg-orange-400 font-semibold rounded-full h-11 px-5"
        >
          <FilePdf size={16} weight="bold" className="mr-2" />
          {downloading ? "Preparing…" : "Download PDF"}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-6">
        {/* Score ring */}
        <div className={`rounded-2xl border p-8 text-center ${scoreRing}`} data-testid="exec-score-card">
          <div className="font-label text-slate-400 mb-3">OVERALL AUDIT READINESS</div>
          <div className={`text-7xl font-display font-black tracking-tight ${scoreColor}`}>{data.audit_readiness_score}%</div>
          <div className="mt-4 inline-flex items-center gap-2 text-sm">
            {trendDelta >= 0 ? (
              <span className="inline-flex items-center gap-1 text-emerald-300">
                <TrendUp size={14} weight="bold" /> +{trendDelta}pts over 12 weeks
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-rose-300">
                <TrendDown size={14} weight="bold" /> {trendDelta}pts over 12 weeks
              </span>
            )}
          </div>
          <div className="grid grid-cols-3 gap-2 mt-6">
            <div className="rounded-lg bg-slate-950 border border-white/10 p-3">
              <div className="text-2xl font-display font-black text-emerald-300">{data.current_credentials}</div>
              <div className="text-[10px] text-slate-500 mt-1 uppercase tracking-wider">Current</div>
            </div>
            <div className="rounded-lg bg-slate-950 border border-white/10 p-3">
              <div className="text-2xl font-display font-black text-amber-300">{data.expiring_credentials}</div>
              <div className="text-[10px] text-slate-500 mt-1 uppercase tracking-wider">Expiring</div>
            </div>
            <div className="rounded-lg bg-slate-950 border border-white/10 p-3">
              <div className="text-2xl font-display font-black text-rose-300">{data.expired_credentials}</div>
              <div className="text-[10px] text-slate-500 mt-1 uppercase tracking-wider">Expired</div>
            </div>
          </div>
        </div>

        {/* Trend chart */}
        <div className="rounded-2xl border border-white/10 bg-slate-900 p-6" data-testid="exec-trend-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="font-label text-slate-400">COMPLIANCE TREND · 12 WEEKS</div>
              <h2 className="text-xl font-display font-black tracking-tight">Audit readiness over time</h2>
            </div>
            <ChartLineUp size={20} className="text-orange-400" weight="bold" />
          </div>
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer>
              <AreaChart data={trend} margin={{ top: 10, right: 12, left: -14, bottom: 0 }}>
                <defs>
                  <linearGradient id="trendGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#f97316" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#f97316" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="week" stroke="#64748b" fontSize={11} tickFormatter={(v) => v.slice(5)} />
                <YAxis domain={[0, 100]} stroke="#64748b" fontSize={11} tickFormatter={(v) => `${v}%`} />
                <Tooltip
                  contentStyle={{ background: "#0f172a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#f1f5f9", fontSize: 12 }}
                  formatter={(v) => [`${v}%`, "Readiness"]}
                />
                <Area type="monotone" dataKey="score" stroke="#f97316" strokeWidth={2} fill="url(#trendGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* By-role breakdown */}
      <div className="rounded-2xl border border-white/10 bg-slate-900 p-6 mt-6" data-testid="exec-role-breakdown">
        <div className="flex items-center gap-2 mb-4">
          <UsersFour size={20} className="text-orange-400" weight="bold" />
          <div>
            <div className="font-label text-slate-400">CREDENTIAL STATUS BY ROLE</div>
            <h2 className="text-xl font-display font-black tracking-tight">Where compliance gaps sit</h2>
          </div>
        </div>
        <div style={{ width: "100%", height: 280 }}>
          <ResponsiveContainer>
            <BarChart data={roleChart} margin={{ top: 10, right: 12, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="role" stroke="#94a3b8" fontSize={12} />
              <YAxis stroke="#64748b" fontSize={11} />
              <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#f1f5f9", fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Current" stackId="a" fill="#10b981" radius={[0, 0, 0, 0]} />
              <Bar dataKey="Expiring" stackId="a" fill="#f59e0b" />
              <Bar dataKey="Expired" stackId="a" fill="#f43f5e" />
              <Bar dataKey="None" stackId="a" fill="#475569" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Upcoming actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
        {[
          { title: "Within 30 days", data: data.upcoming_30, cls: "border-rose-500/25 bg-rose-500/5 text-rose-300", icon: XCircle },
          { title: "Within 60 days", data: data.upcoming_60, cls: "border-amber-500/25 bg-amber-500/5 text-amber-300", icon: Warning },
          { title: "Within 90 days", data: data.upcoming_90, cls: "border-slate-600/50 bg-slate-800/40 text-slate-300", icon: Clock },
        ].map((bucket, i) => {
          const Icon = bucket.icon;
          return (
            <div key={i} className={`rounded-2xl border p-5 ${bucket.cls}`} data-testid={`exec-bucket-${i}`}>
              <div className="flex items-center gap-2 mb-3">
                <Icon size={16} weight="bold" />
                <div className="font-label text-current">{bucket.title.toUpperCase()}</div>
              </div>
              <div className="kpi-number">{bucket.data.length}</div>
              <div className="text-xs mt-2 opacity-75">credentials expiring</div>
              {bucket.data.slice(0, 3).length > 0 && (
                <div className="mt-4 space-y-1 border-t border-white/10 pt-3">
                  {bucket.data.slice(0, 3).map((c, ci) => (
                    <div key={ci} className="text-xs text-slate-400 truncate">
                      <span className="text-slate-200 font-semibold">{c.cred_type}</span> · {c.days}d · <span className="capitalize">{c.staff_role}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Risk staff */}
      <div className="rounded-2xl border border-white/10 bg-slate-900 mt-6 overflow-hidden" data-testid="exec-risk-staff">
        <div className="px-6 py-5 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Sparkle size={16} weight="bold" className="text-orange-400" />
            <div>
              <div className="font-label text-slate-400">TOP RISK STAFF</div>
              <h2 className="text-xl font-display font-black tracking-tight">Who needs attention first</h2>
            </div>
          </div>
        </div>
        {data.risk_staff.length === 0 ? (
          <div className="p-10 text-center">
            <CheckCircle size={40} weight="duotone" className="text-emerald-400 mx-auto mb-3" />
            <div className="text-slate-200 font-semibold">Nothing to flag</div>
            <div className="text-slate-500 text-sm mt-1">Every staff member is up to date.</div>
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {data.risk_staff.map((s) => (
              <Link
                key={s.id}
                to={`/staff/${s.id}`}
                data-testid={`exec-risk-${s.id}`}
                className="flex items-center justify-between px-6 py-4 hover:bg-white/[0.02] transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className={`w-2 h-2 rounded-full ${s.severity === "high" ? "bg-rose-400" : "bg-amber-400"}`} />
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-100">{s.name}</div>
                    <div className="text-xs text-slate-500 capitalize">
                      {s.role} · {s.team || "—"} · {s.credential_count} credential{s.credential_count === 1 ? "" : "s"}
                      {s.next_expiration && <span> · next {s.next_expiration}</span>}
                    </div>
                  </div>
                </div>
                <ArrowRight size={14} weight="bold" className="text-slate-500" />
              </Link>
            ))}
          </div>
        )}
      </div>

      <div className="text-center text-xs text-slate-500 mt-8">
        Generated {new Date(data.generated_at).toLocaleString()}
      </div>
    </div>
  );
}
